import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Cog, Search, LogOut, ShieldCheck, LayoutDashboard, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const navLinks = [
    { to: "/search", label: "Directory" },
    { to: "/pricing", label: "Pricing" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0D14] text-slate-50 relative">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800">
        <div className="px-6 sm:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
              <div className="relative">
                <Cog className="w-7 h-7 text-orange-500 group-hover:rotate-90 transition-transform duration-500" strokeWidth={2.5} />
              </div>
              <div className="font-display font-black uppercase tracking-tight leading-none">
                <div className="text-base sm:text-lg text-slate-50">Vendor Alliance</div>
                <div className="text-[10px] text-orange-500 tracking-[0.3em] -mt-0.5">MVP</div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`text-sm font-semibold uppercase tracking-wider transition-colors ${
                    location.pathname.startsWith(l.to) ? "text-orange-500" : "text-slate-300 hover:text-orange-500"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              {user ? (
                <>
                  {user.role === "admin" ? (
                    <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300 hover:text-orange-500">
                      <ShieldCheck className="w-4 h-4" /> Admin
                    </Link>
                  ) : (
                    <Link to="/dashboard" data-testid="nav-dashboard" className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300 hover:text-orange-500">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                  )}
                  <button
                    data-testid="nav-logout"
                    onClick={() => { logout(); navigate("/"); }}
                    className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300 hover:text-orange-500"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" data-testid="nav-login" className="text-sm font-semibold uppercase tracking-wider text-slate-300 hover:text-orange-500">
                    Login
                  </Link>
                  <Link
                    to="/register"
                    data-testid="nav-list-business"
                    className="px-5 py-2 bg-orange-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-orange-500 transition-colors rounded-sm"
                  >
                    List Your Business
                  </Link>
                </>
              )}
            </nav>

            <button className="md:hidden text-slate-300" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
              {open ? <X /> : <Menu />}
            </button>
          </div>

          {open && (
            <div className="md:hidden border-t border-slate-800 py-4 flex flex-col gap-3">
              {navLinks.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                  {l.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                    {user.role === "admin" ? "Admin" : "Dashboard"}
                  </Link>
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left text-sm font-semibold uppercase tracking-wider text-slate-300">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-semibold uppercase tracking-wider text-slate-300">Login</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-wider text-orange-500">List Your Business</Link>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 relative z-10">{children}</main>

      <footer className="border-t border-slate-800 mt-24 py-12 px-6 sm:px-12 lg:px-24 bg-slate-950">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Cog className="w-6 h-6 text-orange-500" strokeWidth={2.5} />
              <span className="font-display font-black uppercase tracking-tight text-slate-50">Vendor Alliance</span>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed">
              The industrial vendor directory built by contractors, for contractors.
            </p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-orange-600 font-bold mb-4">Platform</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/search" className="hover:text-orange-500">Vendor Directory</Link></li>
              <li><Link to="/pricing" className="hover:text-orange-500">Pricing</Link></li>
              <li><Link to="/register" className="hover:text-orange-500">List Your Business</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-orange-600 font-bold mb-4">For Vendors</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/login" className="hover:text-orange-500">Login</Link></li>
              <li><Link to="/dashboard" className="hover:text-orange-500">Manage Profile</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-orange-600 font-bold mb-4">Contact</h4>
            <p className="text-sm text-slate-400">support@vendoralliance.com</p>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-slate-800 text-xs text-slate-600 uppercase tracking-wider font-mono">
          © {new Date().getFullYear()} Vendor Alliance MVP. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
